import React, { useRef, useState } from 'react'
import betButtonDefaultBg from '@assets/button-betdefault-enabled.svg'
import betButtonForbiddenBg from '@assets/button-betdefault-red-enabled.svg'
import betButtonActiveBg from '@assets/button-betselected-enabled.svg'
import betButtonForbiddenActiveBg from '@assets/button-betselected-red-enabled.svg'

import { cn, useShrinkTextToFit } from '@amusnet/react-ui'
import { useBuyBonusStore, useConfigurationStore, useSpinButtonSectionStore } from '@stores'

type ButtonSize = {
    width: string
    height: string
}

interface BetButtonProps {
    label: string
    amount: string
    isActive?: boolean
    isForbidden: boolean
    onClick?: () => void
    shouldShowCurrency?: boolean
    size?: ButtonSize
    id: string
    className: string
    disableOffsetAnimation?: boolean
}

const BetButton: React.FC<BetButtonProps> = ({
    label,
    amount,
    isActive,
    isForbidden,
    onClick,
    shouldShowCurrency = true,
    size = { width: '136px', height: '96px' },
    className,
    id,
    disableOffsetAnimation = false,
}) => {
    const currency = useConfigurationStore((state) => state.currency)
    const isBetSectionOpened = useSpinButtonSectionStore((state) => state.isBetSectionOpened)
    const isMultiChoiceToggled = useBuyBonusStore((state) => state.isMultiChoiceToggled)
    const { width, height } = size
    const textRef = useRef<HTMLSpanElement>(null)

    const [isPointerDown, setIsPointerDown] = useState(false)

    useShrinkTextToFit({
        refresh: [amount, isBetSectionOpened, isMultiChoiceToggled],
        designFontSize: 46,
        bounds: { width: Number(width.replace('px', '')) - 4 },
        textRef,
    })

    let backgroundImage = isActive ? betButtonActiveBg : betButtonDefaultBg

    if (isForbidden) {
        backgroundImage = isActive ? betButtonForbiddenActiveBg : betButtonForbiddenBg
    }

    const handlePointerDown = (e: React.MouseEvent) => {
        if (e.button !== 0) {
            // If not left click exit
            e.preventDefault()
            e.stopPropagation()

            return
        }

        setIsPointerDown(true)
    }

    return (
        <button
            id={id}
            onClick={onClick}
            className={cn(className, isPointerDown && !disableOffsetAnimation && 'transform: translate-y-[3px]')}
            style={{ width, height }}
            onPointerDown={handlePointerDown}
            onPointerUp={() => setIsPointerDown(false)}
            onPointerLeave={() => setIsPointerDown(false)}
        >
            {/* Background Image */}
            <img
                src={backgroundImage} // Show active or default background
                alt="Bet Button Background"
                className="absolute inset-0 w-full h-full object-cover"
            />

            {/* Currency */}
            {shouldShowCurrency && (
                <span
                    className={cn(
                        isActive ? 'text-[#FFD100]' : 'text-[#cacaca]',
                        'font-bold text-[18px] leading-[16px] mt-[7px] z-10 uppercase'
                    )}
                >
                    {currency}
                </span>
            )}

            {/* Amount */}
            <span
                ref={textRef}
                className={cn(
                    amount === '∞' ? 'mt-[-3px]' : 'mt-[1px]',
                    'font-black text-[46px] leading-[44px] z-10 whitespace-nowrap'
                )}
            >
                {amount}
            </span>

            {/* Label */}
            <span className="text-[#FFD100] font-bold text-[24px] leading-none mt-0 z-10">{label}</span>
        </button>
    )
}

export default BetButton
